import { useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import {
  Building2,
  Wrench,
  CreditCard,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  Lock,
  Tag,
  BarChart3,
  Wallet,
  TrendingUp,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { maskCnpj, cleanCnpj, isValidCnpj } from '@/lib/cnpj';

type Vertical = 'autopecas' | 'financeiro' | 'especialista';

const VERTICALS: { id: Vertical; icon: typeof Wrench; title: string; desc: string }[] = [
  {
    id: 'autopecas',
    icon: Wrench,
    title: 'Autopeças',
    desc: 'Peças e produtos recomendados nas OS e orçamentos das oficinas.',
  },
  {
    id: 'financeiro',
    icon: CreditCard,
    title: 'Financeiro',
    desc: 'Seguros, IPVA, financiamento — ofertas no app do cliente (deveres do veículo).',
  },
  {
    id: 'especialista',
    icon: Sparkles,
    title: 'Especialista',
    desc: 'Som, estética, performance — projetos no app do cliente.',
  },
];

const TOTAL_STEPS = 4;

export function OnboardingPage() {
  const { user, refreshUser, signOut } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [vertical, setVertical] = useState<Vertical>('autopecas');

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/partners/onboarding', {
        companyName: companyName.trim(),
        vertical,
        ...(cleanCnpj(cnpj).length > 0 ? { cnpj: cleanCnpj(cnpj) } : {}),
      });
      return res.data;
    },
  });

  async function finish(target: '/dashboard' | '/balance') {
    if (mutation.isPending) return;
    try {
      await mutation.mutateAsync();
      await refreshUser();
      navigate({ to: target });
    } catch {
      // erro fica visível via mutation.isError no passo final
    }
  }

  // CNPJ é opcional; se preenchido por inteiro tem que passar no mod-11.
  const cnpjDigits = cleanCnpj(cnpj);
  const cnpjInvalid = cnpjDigits.length === 14 && !isValidCnpj(cnpjDigits);
  const cnpjIncomplete = cnpjDigits.length > 0 && cnpjDigits.length < 14;
  const canAdvanceCompany =
    companyName.trim().length > 0 && !cnpjInvalid && !cnpjIncomplete;
  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  return (
    <div className="flex min-h-screen flex-col bg-muted/40">
      {/* Barra de progresso */}
      <div className="h-1 w-full bg-muted">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="mx-auto flex w-full max-w-xl flex-1 flex-col px-6 py-8">
        {/* ── Passo 0: Boas-vindas ───────────────────────── */}
        {step === 0 && (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent text-primary">
              <Building2 size={30} />
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              Bem-vindo ao MustAuto, parceiro!
            </h1>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              Em poucos passos sua marca fica pronta para aparecer no momento de decisão de
              compra real.
            </p>

            <ul className="mt-6 w-full max-w-sm space-y-1 text-left">
              {[
                { icon: Tag, label: 'Dados da empresa e seu segmento' },
                { icon: BarChart3, label: 'Como funciona o seu plano' },
                { icon: Wallet, label: 'Adicionar saldo (quando quiser)' },
              ].map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="flex items-center gap-3 py-1.5 text-sm text-muted-foreground"
                >
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-accent text-primary">
                    <Icon size={15} />
                  </span>
                  {label}
                </li>
              ))}
            </ul>

            {user && (
              <p className="mt-6 text-xs text-muted-foreground">
                Logado como <span className="font-medium text-foreground">{user.name}</span>
              </p>
            )}

            <button
              onClick={() => setStep(1)}
              className="mt-6 flex w-full max-w-sm items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground"
            >
              Vamos começar <ArrowRight size={16} />
            </button>
            <button
              onClick={signOut}
              className="mt-3 text-xs text-muted-foreground hover:text-foreground"
            >
              Usar outra conta
            </button>
          </div>
        )}

        {/* ── Passo 1: Dados + vertical ──────────────────── */}
        {step === 1 && (
          <div className="flex flex-1 flex-col">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Etapa 2 de {TOTAL_STEPS}
            </p>
            <h2 className="mt-1 text-xl font-bold text-foreground">Sua empresa</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              O segmento define como você anuncia. CNPJ é opcional agora.
            </p>

            <div className="mt-5 flex gap-3">
              <div className="flex-1">
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Nome da empresa
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Ex: Distribuidora AutoForte"
                  className="w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
                  autoFocus
                />
              </div>
              <div className="w-44">
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  CNPJ <span className="font-normal text-muted-foreground/70">(opcional)</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={cnpj}
                  onChange={(e) => setCnpj(maskCnpj(e.target.value))}
                  maxLength={18}
                  placeholder="00.000.000/0000-00"
                  aria-invalid={cnpjInvalid}
                  className={`w-full rounded-lg border bg-card px-3 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary ${
                    cnpjInvalid ? 'border-red-500' : 'border-border'
                  }`}
                />
                {cnpjInvalid && (
                  <p className="mt-1 text-xs text-red-500">CNPJ inválido.</p>
                )}
              </div>
            </div>

            <label className="mb-2 mt-5 block text-xs font-medium text-muted-foreground">
              Seu segmento
            </label>
            <div className="space-y-2.5">
              {VERTICALS.map(({ id, icon: Icon, title, desc }) => {
                const selected = vertical === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setVertical(id)}
                    aria-pressed={selected}
                    className={`flex w-full items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${
                      selected
                        ? 'border-primary bg-accent'
                        : 'border-border bg-card hover:border-primary/40'
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
                        selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <Icon size={18} />
                    </span>
                    <span className="flex-1">
                      <span className="block text-sm font-semibold text-foreground">{title}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{desc}</span>
                    </span>
                    {selected && (
                      <Check size={18} className="mt-1 flex-shrink-0 text-primary" strokeWidth={3} />
                    )}
                  </button>
                );
              })}
            </div>

            <WizardFooter
              onBack={() => setStep(0)}
              onNext={() => setStep(2)}
              nextDisabled={!canAdvanceCompany}
              nextLabel="Continuar"
            />
          </div>
        )}

        {/* ── Passo 2: Seu plano (tier) ──────────────────── */}
        {step === 2 && (
          <div className="flex flex-1 flex-col">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Etapa 3 de {TOTAL_STEPS}
            </p>
            <h2 className="mt-1 text-xl font-bold text-foreground">Seu plano hoje</h2>
            <span className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
              🥉 Plano Bronze
            </span>
            <p className="mt-3 text-sm text-muted-foreground">
              Você já pode preparar tudo e começar a aparecer nas oficinas. Para alcançar o app do
              cliente e recursos avançados, é só evoluir de plano.
            </p>

            <ul className="mt-4 space-y-1">
              <PlanRow ok>Cadastrar dados, catálogo e campanhas</PlanRow>
              <PlanRow ok>Aparecer nas Ordens de Serviço das oficinas</PlanRow>
              <PlanRow>Aparecer nos orçamentos e no app do cliente — a partir do Prata</PlanRow>
              <PlanRow>Inteligência de mercado e oportunidades — Prata/Ouro</PlanRow>
            </ul>

            <div className="mt-4 flex items-start gap-2 rounded-xl border border-primary/20 bg-accent p-3.5 text-xs leading-relaxed text-accent-foreground">
              <TrendingUp size={16} className="mt-0.5 flex-shrink-0" />
              <span>
                <span className="font-semibold">Como evoluir:</span> o plano sobe automaticamente
                conforme seu investimento, ou você pode solicitar upgrade a qualquer momento em
                Configurações.
              </span>
            </div>

            <WizardFooter
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
              nextLabel="Entendi, continuar"
            />
          </div>
        )}

        {/* ── Passo 3: Saldo (opcional) ──────────────────── */}
        {step === 3 && (
          <div className="flex flex-1 flex-col">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Etapa 4 de {TOTAL_STEPS}
            </p>
            <h2 className="mt-1 text-xl font-bold text-foreground">Adicionar saldo</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              O saldo pré-pago abastece suas campanhas. Você pode pular e fazer isso depois — sem
              saldo, as campanhas ficam pausadas até o primeiro depósito.
            </p>

            <div className="mt-5 flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card/60 p-6 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-primary">
                <Wallet size={22} />
              </span>
              <p className="mt-3 max-w-xs text-sm text-muted-foreground">
                Faça seu primeiro depósito via PIX, cartão ou boleto na tela de Saldo. Escolha o
                valor e a forma de pagamento por lá.
              </p>
            </div>

            {mutation.isError && (
              <p className="mt-3 text-center text-sm text-destructive">
                Não foi possível concluir o cadastro. Tente novamente.
              </p>
            )}

            <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
              <button
                onClick={() => finish('/dashboard')}
                disabled={mutation.isPending}
                className="rounded-lg px-4 py-2.5 text-sm font-semibold text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                Adiciono depois
              </button>
              <button
                onClick={() => finish('/balance')}
                disabled={mutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                {mutation.isPending ? 'Concluindo...' : 'Adicionar saldo'}
                {!mutation.isPending && <ArrowRight size={16} />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function WizardFooter({
  onBack,
  onNext,
  nextLabel,
  nextDisabled = false,
}: {
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
  nextDisabled?: boolean;
}) {
  return (
    <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={16} /> Voltar
      </button>
      <button
        onClick={onNext}
        disabled={nextDisabled}
        className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
      >
        {nextLabel}
      </button>
    </div>
  );
}

function PlanRow({ ok = false, children }: { ok?: boolean; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 py-1 text-sm">
      {ok ? (
        <Check size={16} className="mt-0.5 flex-shrink-0 text-success" strokeWidth={3} />
      ) : (
        <Lock size={15} className="mt-0.5 flex-shrink-0 text-muted-foreground" />
      )}
      <span className={ok ? 'text-foreground' : 'text-muted-foreground'}>{children}</span>
    </li>
  );
}
